import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useTest } from '../../context/test';
import { useAuth } from '../../hooks/use-auth';
import * as testsService from '../../services/api/tests';
import { PatientOrder } from '../../services/api/tests';
import { Test } from '../../entities/test';
import CommonAnimatedSpinner from '../../components/common/animated/spinner';
import TestStatusBadge from '../../components/tests/status-badge';
import OrderStatusBadge from '../../components/orders/status-badge';

function TestsResultsOtherTestsPageComponent() {
  const { token } = useAuth();
  const { testView } = useTest();
  const [tests, setTests] = useState<Test[]>([]);
  const [orders, setOrders] = useState<PatientOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPatientHistory = async () => {
      if (testView?.test.patientHash) {
        setIsLoading(true);

        const testsResponse = await testsService.list(token, {
          patientHash: testView.test.patientHash,
          excludeTestId: testView.test.id,
          limit: 50,
        });

        setTests(testsResponse.tests);

        if (testView.order?.patientId) {
          const ordersResponse = await testsService.getPatientOrders(
            token,
            testView.order.patientId,
            testView.test.orderId,
          );

          setOrders(ordersResponse.orders);
        }

        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    };

    fetchPatientHistory();
  }, [token, testView?.test.patientHash, testView?.test.id, testView?.order?.patientId, testView?.test.orderId]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <CommonAnimatedSpinner />
      </div>
    );
  }

  if (!testView?.test.patientHash) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <p className="text-gray-500">No patient linked to this test.</p>
      </div>
    );
  }

  if (tests.length === 0 && orders.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <p className="text-gray-500">No other tests or orders found for this patient.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {tests.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Other Tests ({tests.length})</h3>
            <p className="mt-1 text-sm text-gray-500">
              Tests for patient {testView.test.patientName} {testView.test.patientLastName}
            </p>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Test ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Doctor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tests.map((test) => (
                <tr key={test.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <Link
                      to={`/tests/results/${test.id}`}
                      className="text-indigo-600 hover:text-indigo-900 font-medium"
                    >
                      #{test.id}
                    </Link>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {test.productTitle || test.productKey || 'N/A'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <TestStatusBadge status={test.status} />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {test.administratorFirstName && test.administratorLastName
                      ? `${test.administratorFirstName} ${test.administratorLastName}`
                      : 'Unassigned'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {test.createdAt?.slice(0, 10) || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {orders.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Other Orders ({orders.length})</h3>
            <p className="mt-1 text-sm text-gray-500">Orders without tests (consultations, bundles, etc.)</p>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <Link
                      to={`/orders/results/${order.id}`}
                      className="text-indigo-600 hover:text-indigo-900 font-medium"
                    >
                      #{order.id}
                    </Link>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {order.productTitle || order.productKey || 'N/A'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <OrderStatusBadge status={order.status} />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {order.createdAt?.slice(0, 10) || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TestsResultsOtherTestsPageComponent;
